import React from 'react';

/**
 * `value`, but only after it has stopped changing for `delayMs`.
 *
 * The input it belongs to stays **fully controlled by the undebounced value** —
 * this is what goes into a query key or a request body, never into the control.
 * Debouncing the control itself makes typing feel broken; debouncing what it
 * triggers is the whole point.
 *
 * Promoted here from `useSalariesPage` and `useFixedAssetsPage`, which each held
 * a private copy and both said to promote it when a third arrived. The Record
 * tab is the third, and its copy would have been the one that mattered most: it
 * debounces a *posting preview*, so two implementations drifting would mean the
 * panel and the Post button disagreeing about which request is in flight.
 */
export const useDebouncedValue = <T>(value: T, delayMs: number): T => {
    const [settled, setSettled] = React.useState(value);

    React.useEffect(() => {
        const timer = setTimeout(() => setSettled(value), delayMs);
        return () => clearTimeout(timer);
    }, [value, delayMs]);

    return settled;
};
